import { useEffect, useState } from 'react';
import { admin_api } from '@/lib/api';
import { toast } from 'sonner';
import { Trash, Plus, PencilSimple, X, IdentificationBadge } from '@phosphor-icons/react';

const EMPTY = { full_name: '', email: '', phone: '', license_number: '', license_expiry: '', wwcc_expiry: '', first_aid_expiry: '', status: 'off_shift', notes: '', password: '' };
const STATUSES = ['on_shift', 'off_shift', 'leave', 'inactive'];
const STATUS_STYLE = {
  on_shift: 'bg-green-100 text-green-700',
  off_shift: 'bg-neutral-200 text-neutral-600',
  leave: 'bg-amber-100 text-amber-700',
  inactive: 'bg-red-100 text-red-600',
};

export default function AdminDrivers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const res = await admin_api.driversList(); setItems(res.items || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await admin_api.driverUpdate(editingId, form); toast.success('Driver updated.'); }
      else { await admin_api.driverCreate(form); toast.success('Driver added. They can log in at /driver/login.'); }
      setForm(EMPTY); setEditingId(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save driver.');
    }
  };

  const edit = (d) => {
    setEditingId(d.id);
    setForm({ ...EMPTY, ...Object.fromEntries(Object.entries(d).filter(([k]) => k in EMPTY)), password: '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const setStatus = async (d, status) => {
    try { await admin_api.driverUpdate(d.id, { ...d, status, password: '' }); toast.success(`${d.full_name} → ${status}`); load(); }
    catch { toast.error('Could not update status.'); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this driver?')) return;
    try { await admin_api.driverDelete(id); toast.success('Deleted.'); load(); }
    catch { toast.error('Could not delete.'); }
  };

  return (
    <div data-testid="admin-drivers">
      <div className="mb-8">
        <div className="overline mb-2">Operations</div>
        <h1 className="font-display font-extrabold text-3xl text-ink">Drivers</h1>
        <p className="text-ink-muted text-sm mt-1">Drivers marked on-shift are offered first for assignments. Each driver gets a portal login at /driver/login.</p>
      </div>

      <form onSubmit={submit} className="p-6 rounded-2xl bg-white border border-neutral-200 mb-8 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="admin-driver-form">
        <div className="md:col-span-3 flex items-center gap-2 text-sm font-semibold text-ink">
          <IdentificationBadge size={18} weight="duotone" className="text-brand" />
          {editingId ? 'Edit driver' : 'Add a driver'}
          {editingId && (
            <button type="button" onClick={() => { setEditingId(null); setForm(EMPTY); }} data-testid="admin-driver-cancel-edit" className="ml-auto inline-flex items-center gap-1 text-xs text-ink-muted hover:text-ink">
              <X size={12} /> Cancel edit
            </button>
          )}
        </div>
        <div><label className="label-base">Full name</label><input required value={form.full_name} onChange={set('full_name')} data-testid="admin-driver-name" className="input-base" /></div>
        <div><label className="label-base">Email (login)</label><input required type="email" value={form.email} onChange={set('email')} data-testid="admin-driver-email" className="input-base" /></div>
        <div><label className="label-base">Phone</label><input required value={form.phone} onChange={set('phone')} data-testid="admin-driver-phone" className="input-base" /></div>
        <div><label className="label-base">Licence number</label><input value={form.license_number} onChange={set('license_number')} data-testid="admin-driver-license" className="input-base" /></div>
        <div><label className="label-base">Licence expiry</label><input type="date" value={form.license_expiry} onChange={set('license_expiry')} data-testid="admin-driver-license-expiry" className="input-base" /></div>
        <div><label className="label-base">WWCC expiry</label><input type="date" value={form.wwcc_expiry} onChange={set('wwcc_expiry')} data-testid="admin-driver-wwcc-expiry" className="input-base" /></div>
        <div><label className="label-base">First aid expiry</label><input type="date" value={form.first_aid_expiry} onChange={set('first_aid_expiry')} data-testid="admin-driver-firstaid-expiry" className="input-base" /></div>
        <div>
          <label className="label-base">Status</label>
          <select value={form.status} onChange={set('status')} data-testid="admin-driver-status" className="input-base">
            {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
          </select>
        </div>
        <div><label className="label-base">{editingId ? 'New password (optional)' : 'Portal password'}</label><input value={form.password} onChange={set('password')} data-testid="admin-driver-password" className="input-base" placeholder={editingId ? 'Leave blank to keep current' : 'Default: SwiftDriver2026!'} /></div>
        <div className="md:col-span-3 flex justify-end">
          <button type="submit" data-testid="admin-driver-submit" className="btn-primary !py-2.5 !px-5 text-sm">
            {editingId ? <><PencilSimple size={14} weight="bold" /> Save changes</> : <><Plus size={14} weight="bold" /> Add driver</>}
          </button>
        </div>
      </form>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-driver-list">
        {loading && <div className="text-ink-muted">Loading…</div>}
        {!loading && items.length === 0 && <div className="text-ink-muted">No drivers yet — add your first driver above.</div>}
        {items.map(d => (
          <div key={d.id} className="p-5 rounded-2xl bg-white border border-neutral-200" data-testid={`admin-driver-card-${d.id}`}>
            <div className="flex items-center justify-between mb-2">
              <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${STATUS_STYLE[d.status] || ''}`}>{d.status.replace('_', ' ')}</span>
              <div className="flex items-center gap-2">
                <button onClick={() => edit(d)} data-testid={`admin-driver-edit-${d.id}`} className="text-ink-muted hover:text-brand" aria-label="Edit"><PencilSimple size={16} /></button>
                <button onClick={() => del(d.id)} data-testid={`admin-driver-delete-${d.id}`} className="text-ink-muted hover:text-red-500" aria-label="Delete"><Trash size={16} /></button>
              </div>
            </div>
            <div className="font-display font-bold text-lg text-ink">{d.full_name}</div>
            <div className="text-xs text-ink-muted mt-0.5">{d.email} · {d.phone}</div>
            <div className="mt-3 space-y-1 text-xs text-ink-soft">
              {d.license_expiry && <div>Licence expires {d.license_expiry}</div>}
              {d.wwcc_expiry && <div>WWCC expires {d.wwcc_expiry}</div>}
              {d.first_aid_expiry && <div>First aid expires {d.first_aid_expiry}</div>}
            </div>
            <div className="mt-4">
              <select value={d.status} onChange={e => setStatus(d, e.target.value)} data-testid={`admin-driver-status-select-${d.id}`} className="input-base !py-1.5 text-xs">
                {STATUSES.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
              </select>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
